import { type FC } from "react";

import { type DraftCitationFragment, DraftingSourceEnum } from "@app_schema";

import { Select } from "@styled/select";

import { CitedPages, CitedPagesList } from "./cited_pages";
import { type DraftingSections } from "./use_drafting_sections";

// What a Drafting section can read, in the order the picker lists them.
const SOURCES: ReadonlyArray<{
  value: DraftingSourceEnum;
  label: string;
  hint?: string;
}> = [
  {
    value: DraftingSourceEnum.Intake,
    label: "Intake",
    hint: "answers and flags",
  },
  {
    value: DraftingSourceEnum.Dictation,
    label: "Dictation",
    hint: "the cleaned transcript",
  },
  {
    value: DraftingSourceEnum.Records,
    label: "Records",
    hint: "record summaries",
  },
  { value: DraftingSourceEnum.Supporting, label: "Supporting documents" },
  {
    value: DraftingSourceEnum.Notes,
    label: "Your notes",
    hint: "typed on each case",
  },
];

// A drafted section's citations of sources it doesn't read, and what it reads
// (`reads`, as a sentence ends), as its row and the answer's list show them.
export const CitationsOutside: FC<{
  citations: readonly DraftCitationFragment[];
  reads: string;
}> = ({ citations, reads }) => (
  <>
    <CitedPagesList citations={citations} /> (reads only {reads})
  </>
);

// A drafted section's citations of sources it doesn't read, as one line.
export const citationsLine = (
  citations: readonly DraftCitationFragment[],
  reads: string,
) => {
  const [first] = citations;
  if (citations.length === 1 && first)
    return (
      <>
        Cites <CitedPages citation={first} />, but this section reads only{" "}
        {reads}.
      </>
    );

  return `${String(citations.length)} citations from sources this section doesn't read`;
};

// The picker's options, each checked when the section reads it.
export const sourceOptions = (sources: ReadonlyArray<DraftingSourceEnum>) =>
  SOURCES.map((source) => ({
    ...source,
    checked: sources.includes(source.value),
  }));

// Under a section's sources: why one can't be read, that his draft always is,
// and where a change is saved.
export const SourcesFooter: FC<{
  note?: string | null;
  more?: string;
  onTemplate: boolean;
}> = ({ note, more, onTemplate }) => (
  <>
    {note && <p>{note}</p>}
    <p>Your own draft text is always read.{more && ` ${more}`}</p>
    {onTemplate && <p>Saved to your template for new cases.</p>}
  </>
);

// The sources with `value` turned on or off, in the picker's order.
export const toggled = (
  sources: ReadonlyArray<DraftingSourceEnum>,
  value: DraftingSourceEnum,
) =>
  SOURCES.map((source) => source.value).filter((source) =>
    source === value ? !sources.includes(source) : sources.includes(source),
  );

// All of the case's supporting documents, or the one he picks.
export const SupportingDocumentSelect: FC<{
  documents: DraftingSections["supportingDocuments"];
  value: string | null | undefined;
  onChange(id: string | null): void;
}> = ({ documents, value, onChange }) => (
  <Select
    variant="sm"
    aria-label="Supporting documents to read"
    value={value ?? ""}
    onChange={(event) => onChange(event.target.value || null)}
  >
    <option value="">All supporting documents</option>
    {documents.map((document) => (
      <option key={document.id} value={document.id}>
        {document.name}
      </option>
    ))}
  </Select>
);
